//Please don't import anything in this file, this file is also used in SNF

import { Cents, CurrencyMinor, ZERO_CENTS } from '@fox/atoms';

export enum PaymentMethod {
	Cash = 'CASH',
	Cheque = 'CHEQUE',
	CreditCard = 'CREDIT_CARD',
	DirectDebit = 'DIRECT_DEBIT',
	Generic = 'GENERIC',
	PayrollDeduction = 'PAYROLL_DEDUCTION',
	Statement = 'STATEMENT',
	OneTimePayment = 'ONE_TIME_PAYMENT'
}

export enum ProviderCapability {
	AddMethod = 'ADD_METHOD',
	CanEditExpiry = 'CAN_EDIT_EXPIRY',
	Customer = 'CUSTOMER',
	ImmediatePayment = 'IMMEDIATE_PAYMENT',
	ImmediateRefund = 'IMMEDIATE_REFUND',
	OutOfBand = 'OUT_OF_BAND',
	RecordPayment = 'RECORD_PAYMENT',
	Schedules = 'SCHEDULES'
}

export type ProviderSelection = {
	name: string;
	method: PaymentMethod;
	provider: string;
	providerExtension: string;
	instanceId?: string | null | undefined;
	capabilities: Array<ProviderCapability>;
	settings?: string | null | undefined;
};

export type SetupIntentResult = {
	key?: string | null | undefined;
	error?: string | null | undefined;
	intentId?: string | null | undefined;
	data?:
		| { baseUrl: string; returnPath: string } //FatZebra
		| { timestamp: string; fingerprint?: string | null | undefined; returnUrl: string } //NAB
		| { environment: string; clientId: string } //PayPal
		| { name: string } //EziDebit
		| null
		| undefined;
};

export type PaymentAddProps = {
	provider: ProviderSelection;
	onSuccess: (id: number | string) => void;
	onCancel?: () => void;
	settings?: unknown;
	showSave?: boolean;
	meta?: Record<string, unknown>;
};

export type PaymentAddIntentProps = {
	component: React.FC<PaymentAddComponentProps>;
	provider: ProviderSelection;
	onSuccess: (id: number) => void;
	onCancel?: () => void;
	settings?: unknown;
	getIntent: () => Promise<SetupIntentResult | undefined>;
	completeIntent: (intentId: string, data: string) => Promise<number | undefined>;
	updateIntent?: (intentId: string, amount: number) => Promise<SetupIntentResult | undefined>;
	showSave?: boolean;
	disableOnError?: boolean;
};

export type PaymentOptionalCvv = { optionalCvv?: true };
export type PaymentAddComponentProps<TSettings = unknown> = {
	onSuccess: (id: number) => void;
	onCancel?: () => void;
	settings?: TSettings;
	completeIntent: (intentId: string, data: string) => Promise<number | undefined>;
	updateIntent?: (intentId: string, amount: number) => Promise<SetupIntentResult | undefined>;
	intentData: SetupIntentResult | undefined;
	showSave?: boolean;
};

export enum CreditCardType {
	Amex = 'AMEX',
	Discover = 'DISCOVER',
	Jcb = 'JCB',
	Mastercard = 'MASTERCARD',
	Other = 'OTHER',
	UnionPay = 'UNION_PAY',
	Unspecified = 'UNSPECIFIED',
	Visa = 'VISA'
}

export type CreditCard = {
	id: number | string;
	type: CreditCardType;
	provider: string;
	maskedNumber: string;
	inUse: boolean;
	expires?: { month: any; year: number } | null;
};
export type DirectDebit = {
	id: number | string;
	bsb: string;
	provider: string;
	account: string;
	accountName: string;
	inUse: boolean;
};
export type Customer = {
	id: number | string;
	customerId: string;
	defaultCreditCardId?: number | null;
	creditCards: Array<CreditCard>;
	directDebits: Array<DirectDebit>;
};

export type CreditCardDisplayProps = {
	card: CreditCard;
	isDefault?: boolean;
	resolveProvider?: (payment: PaymentType) => string;
	meta?: Record<string, unknown>;
};

export type DirectDebitDisplayProps = {
	debit: DirectDebit;
	resolveProvider?: (payment: PaymentType) => string;
	meta?: Record<string, unknown>;
};
export type PaymentSelectorProps<PT = PaymentType> = {
	customers: Customer[];
	provider: ProviderSelection;
	setSelectedPayment: (selectedPayment: PT) => void;
	selectedPayment?: PT;
	onAddPayment?: (payment: PT) => void;
	allowAddPaymentMethod?: boolean;
	creditCardComponent?: React.ComponentType<CreditCardDisplayProps>;
	directDebitComponent?: React.ComponentType<DirectDebitDisplayProps>;
	/**Resolve and show the provider name on the 'Add Credit Card/Direct Debit' buttons */
	resolveProvider?: (payment: PT) => string;
	meta?: Record<string, unknown>;
};

export type PaymentSelection = { method: PaymentMethod; provider: string };

export type CreditCardId = PaymentSelection & { creditCardId: number; method: PaymentMethod.CreditCard };
export type DirectDebitId = PaymentSelection & { directDebitId: number; method: PaymentMethod.DirectDebit };
export type ManualPaymentId = PaymentSelection & { paymentConfig: string };
export type GenericPayment = PaymentSelection;

export type OneTimePayment = PaymentSelection;

export type PaymentType = CreditCardId | DirectDebitId | PaymentSelection | ManualPaymentId | GenericPayment | OneTimePayment;

export type Payer = { id: number; name: string; hasConfiguredMapping: boolean };

export interface Bank {
	bsb: string;
	account: string;
	name: string;
}

export interface Expiry {
	month: string;
	year: string;
}

export interface Card {
	number: string;
	expiry: Expiry;
	name: string;
	scheme: string;
	localisation: string;
	type: string;
}

export interface AttachPaymentResult {
	isToken: boolean;
	card: Card;
	bank: Bank;
	accepted: boolean;
	surcharge: number;
	provider: string;
}
type CardSettings = {
	/** @default true */
	enabled: boolean;
	number?: { label?: string };
	expiry?: { label?: string };
	name?: { label?: string; hide: boolean };
};
type BankSettings = { /** @default false*/ enabled: boolean };
export type BPointClientAPI = {
	token: {
		authkey: {
			setupPaymentMethodForm(
				authkey: string,
				options: {
					appendToElementId: string;
					card?: CardSettings;
					bank?: BankSettings;
					clientsideValidation?: boolean;
					onFormLoaded?: () => void;
					callback: (result: 'success' | 'error' | 'timeout', data: AttachPaymentResult) => void;
				}
			);
			attachPaymentMethod(
				authkey: string,
				paymentMethod: {
					card?: { number: string; expiry: { month: string; year: string }; name: string };
					bank?: { bsb: string; account: string; name: string };
				},
				callback: (result: 'success' | 'error' | 'timeout', data: AttachPaymentResult) => void
			);
		};
	};
};

export type FatZebraResponse = {
	card_expiry: string;
	card_holder: string;
	card_number: string;
	token: string;
	v: string;
	r: number; //response code, 1 = success, anything else is an error
};

export enum PlanType {
	Anniversary = 'ANNIVERSARY',
	AnniversaryRounded = 'ANNIVERSARY_ROUNDED',
	Common = 'COMMON',
	CommonRenewal = 'COMMON_RENEWAL'
}

export const FRIENDLY_PLAN_TYPES: Record<PlanType, string> = {
	[PlanType.Common]: 'Common',
	[PlanType.Anniversary]: 'Anniversary',
	[PlanType.AnniversaryRounded]: 'Anniversary Rounded',
	[PlanType.CommonRenewal]: 'Common Renewal'
};

export function calculateOption(amount: CurrencyMinor | number | undefined, type: PlanOptionType, fee: Cents) {
	if (!amount) return ZERO_CENTS;

	if (type === PlanOptionType.Discount) return -(amount as number) as CurrencyMinor;
	if (type === PlanOptionType.Extra) return amount as CurrencyMinor;
	// To allow for decimal discounts, we are storing as 100x percentage
	//We need to formalise rounding rules in frontend
	if (type === PlanOptionType.DiscountPercent) return -Math.round((amount * fee) / 10000) as Cents;
	return ZERO_CENTS;
}

export enum PlanOptionType {
	Discount = 'DISCOUNT',
	Extra = 'EXTRA',
	DiscountPercent = 'DISCOUNT_PERCENT'
}

export type PlansQuery = {
	all_plans: Array<{
		id: number;
		externalProviderId?: string | null;
		name: string;
		type: PlanType;
		planKey: string;
		category?: string | null;
		multiple: boolean;
		allowNominated: boolean;
		requirements?: Array<{ type: string; fieldId: string; values: Array<string> }> | null;
		fees: Array<{
			intervalOffset?: number | null;
			id: number;
			renewalFee?: Cents | null;
			fee: Cents;
			planId: number;
			currency: string;
			interval: Interval;
			prorate?: Interval | null;
			units?: number | null;
			effectiveFrom: string;
			archivedAt?: string | null | undefined;
		}>;
		options: Array<{
			id: number;
			name: string;
			amount: number;
			interval?: Interval | null;
			type: PlanOptionType;
			units?: number | null;
			allowLimit: boolean;
			defaultLimit?: number | null;
		}>;
		earlyPeriod?: { day: any; month: any } | null;
		commonPeriod?: { day: any; month: any } | null;
		graceExpiry?: { day: any; month: any } | null;
		archivedAt?: string | null | undefined;
	}>;
};

export enum Interval {
	Daily = 'DAILY',
	Annual = 'ANNUAL',
	Fortnightly = 'FORTNIGHTLY',
	FourWeekly = 'FOUR_WEEKLY',
	Monthly = 'MONTHLY',
	Quarterly = 'QUARTERLY',
	Weekly = 'WEEKLY'
}

export const FRIENDLY_INTERVALS: Record<Interval, string> = {
	[Interval.Daily]: 'Daily',
	[Interval.Annual]: 'Annual',
	[Interval.Monthly]: 'Monthly',
	[Interval.Quarterly]: 'Quarterly',
	[Interval.Weekly]: 'Weekly',
	[Interval.Fortnightly]: 'Fortnightly',
	[Interval.FourWeekly]: 'Four Weekly'
};

export const INTERVAL_SHORT: Record<Interval, string> = {
	[Interval.Daily]: 'daily',
	[Interval.Monthly]: 'month',
	[Interval.Fortnightly]: 'fortnight',
	[Interval.Weekly]: 'week',
	[Interval.Quarterly]: 'quarter',
	[Interval.Annual]: 'year',
	[Interval.FourWeekly]: 'four week'
};
export const INTERVAL_NAMESLY: Record<Interval, string> = {
	[Interval.Daily]: 'Daily',
	[Interval.Monthly]: 'Monthly',
	[Interval.Fortnightly]: 'Fortnightly',
	[Interval.Weekly]: 'Weekly',
	[Interval.Quarterly]: 'Quarterly',
	[Interval.Annual]: 'Yearly',
	[Interval.FourWeekly]: 'Four Weekly'
};

export type FinancialConfigCommon = {
	allowedCurrencies: string[];
	defaults: {
		account: number;
		currency: string;
		discountItem: number;
		feeItem: number;
		formTransactionItem: number;
		invoiceDueDays: number;
		taxInclusive: boolean;
		taxRate: number;
		ticketItem: number;
	};
	explicitProviders: boolean;
	grid?: unknown;
	matchPaths?: string[];
	referenceSelector?: string;
	showCharges?: boolean;
	taxLabel?: string;
	reportingYear?: 'calendar' | 'financial';
	invoiceHeaderLabel?: string;
};
export type FormFinancialLineItem = {
	amount: Cents;
	comment: string;
	lineTypeId: number;
	immediatePayment: boolean;
	autoRenew?: boolean;
	quantity?: number;
	unitAmount?: number;
	invoice?: {
		item?: number | null;
		account?: number | null;
		taxRate?: number | null;
		taxInclusive?: boolean | null;
		trackingCodes?: TrackingCodeMetadata[];
	};
	targetId?: string;
	targetType?: AppliesToType;
	code?: string;
};

export type TrackingCodeMetadata = {
	categoryName: string;
	codeCode: string;
	codeName: string;
	trackingCategoryId: number;
	trackingCodeId: number;
};

export enum IntegrationType {
	Xero = 'xero'
}
export enum IntegrationTypeFriendly {
	xero = 'Xero'
}

export enum StandardLineType {
	Payment = 1,
	Subscription = 2,
	Adjustment = 3,
	Discount = 4,
	SubscriptionRenewal = 5,
	Tax = 6,
	Ticket = -1
}

// This originates from gql generated in fox-financial
export enum AppliesToType {
	Subscription = 'SUBSCRIPTION',
	Ticket = 'TICKET'
}

// This originates from gql generated in fox-financial
export enum DiscountType {
	FixedRate = 'FIXED_RATE',
	Percentage = 'PERCENTAGE'
}

export type DiscountCodeModel = {
	__typename?: 'DiscountCodeModel';
	code: string;
	current?: number;
	enabled: boolean;
	id: number;
	useLimit?: number;
};

export type RedeemedCodesModel = {
	__typename?: 'RedeemedCodesModel';
	codeId: number;
	discountId: number;
	id: number;
	ownerId: string;
	ownerType: string;
	redeemed: Date;
};

export type DiscountFragment = {
	enabled: boolean;
	id: number;
	discountType: DiscountType;
	amount: number;
	singleUse: boolean;
	description?: string | null;
	current?: number | null;
	limit?: number | null;
	ownerId: string;
	ownerType: string;
	validFrom?: any | null;
	validTo?: any | null;
	accountId?: number | null;
	itemId?: number | null;
	taxInclusive?: boolean | null;
	taxRateId?: number | null;
	appliesTo: Array<{ __typename?: 'AppliesToItemModel'; targetType: AppliesToType; targetId?: string | null }>;
	trackingCodes?: string;
	codes: Array<{
		__typename?: 'DiscountCodeModel';
		code: string;
		enabled: boolean;
		id: number;
		useLimit?: number | null;
		current?: number | null;
	}>;
	redeemedCodes: Array<{
		__typename?: 'RedeemedCodesModel';
		ownerId: string;
		ownerType: string;
		codeId: number;
		id: number;
		redeemed: any;
		discountId: number;
	}>;
};
